import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Server, Shield, Zap, Globe, Clock, Lock } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteHosting = () => {
  const faqs = [
    {
      question: "What is lawn care website hosting?",
      answer: "Website hosting is the service that keeps your site online and accessible. It provides the server space where your website files live so visitors can access your lawn care site 24/7."
    },
    {
      question: "How much does lawn care website hosting cost?",
      answer: "Basic shared hosting costs $3–$15/month. Quality managed hosting runs $20–$50/month. Our Core Website plan at $200/month includes hosting—no separate hosting fee."
    },
    {
      question: "Is hosting included in your subscription?",
      answer: "Yes. Hosting, SSL certificates, and server maintenance are all included in the $200/month Core Website plan. You don't manage any hosting separately."
    },
    {
      question: "What makes good hosting for lawn care websites?",
      answer: "Fast load speeds (especially on mobile), reliable uptime, SSL security, and CDN for geographic coverage. Cheap hosting often means slow sites that lose leads."
    },
    {
      question: "Do I need to buy hosting separately if I build my own site?",
      answer: "Yes. DIY site builders often include hosting, but standalone WordPress sites require separate hosting. Quality hosting for a business site typically costs $15–$50/month."
    },
    {
      question: "What's the difference between shared and managed hosting?",
      answer: "Shared hosting puts many sites on one server (cheaper, slower). Managed hosting provides dedicated resources, faster speeds, automatic updates, and technical support."
    },
    {
      question: "Does hosting affect my Google rankings?",
      answer: "Yes. Site speed is a ranking factor. Slow hosting = slow site = lower rankings. Fast, reliable hosting helps your lawn care website perform better in search results."
    },
    {
      question: "What happens if my hosting goes down?",
      answer: "With cheap hosting, you're on your own. With managed hosting or our subscription, issues are monitored and resolved quickly—often before you notice."
    },
    {
      question: "Can I switch hosting providers later?",
      answer: "Yes, but it's technical and risky if done wrong. With our subscription, we handle all hosting—you never need to think about it or switch providers."
    },
    {
      question: "Do I need an SSL certificate with hosting?",
      answer: "Yes. SSL (the 'https' and padlock) is required for security and Google rankings. Quality hosting includes free SSL. Our subscription includes SSL automatically."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-hosting/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website hosting for lawn care companies included in our website subscription service."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-hosting/#webpage",
    "name": "Lawn Care Website Hosting | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-hosting/",
    "description": "Fast, reliable lawn care website hosting included in our subscription. SSL, CDN, and uptime monitoring—no separate hosting fees.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-hosting/#service",
    "name": "Lawn Care Website Hosting",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Managed website hosting for lawn care companies with fast speeds, SSL security, and reliable uptime.",
    "areaServed": "United States",
    "serviceType": "Website Hosting"
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Hosting | Leaf Rush"
        description="Fast, reliable lawn care website hosting included in our subscription. SSL, CDN, and uptime monitoring—no separate hosting fees."
        keywords="lawn care website hosting, lawn care site hosting, web hosting lawn care, lawn care server hosting"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-hosting/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-hosting/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Included in Subscription
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website Hosting That's Fast and Reliable
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Fast loading, reliable uptime, SSL security, and CDN—all included in your monthly subscription. No separate hosting fees or technical headaches.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Lawn care website hosting keeps your site online and fast. Standalone hosting costs $15–$50/month for quality service. Our subscription includes managed hosting in the $200–$300/month price.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're looking for a complete website solution, visit our main{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What's Included in Our Hosting
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Fast Load Speeds</p>
                    <p className="text-sm text-muted-foreground">
                      Optimized servers and CDN for fast loading—especially important for mobile visitors.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Server className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Reliable Uptime</p>
                    <p className="text-sm text-muted-foreground">
                      99.9% uptime monitoring so your site is always available when leads search.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">SSL Security</p>
                    <p className="text-sm text-muted-foreground">
                      HTTPS encryption included—required for security and Google rankings.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">CDN (Content Delivery)</p>
                    <p className="text-sm text-muted-foreground">
                      Content delivered from servers closest to your visitors for faster loading.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Hosting Matters */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Quality Hosting Matters for Lawn Care Websites
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Mobile Speed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Most lawn care leads come from phones. Slow hosting = slow site = lost leads.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Google Rankings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Site speed is a ranking factor. Fast hosting helps you rank higher in local searches.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Always Available</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      If your site is down when someone searches, you lose that lead to a competitor.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Hosting Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Hosting Options Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Monthly Cost</th>
                      <th className="text-left py-3 px-4">Speed</th>
                      <th className="text-left py-3 px-4">Support</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">Included in $200–$300</td>
                      <td className="py-3 px-4">Fast (managed)</td>
                      <td className="py-3 px-4">Full support</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Managed Hosting</td>
                      <td className="py-3 px-4">$20–$50</td>
                      <td className="py-3 px-4">Fast</td>
                      <td className="py-3 px-4">Technical support</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Shared Hosting</td>
                      <td className="py-3 px-4">$3–$15</td>
                      <td className="py-3 px-4">Variable (often slow)</td>
                      <td className="py-3 px-4">Limited</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/lawn-care-website-cost/" className="text-primary underline hover:no-underline">
                  lawn care website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Lawn Care Website Hosting
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional lawn care websites.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for lawn care sites.</p>
                </NavLink>
                <NavLink to="/service-pages-that-rank/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Service Pages That Rank</h3>
                  <p className="text-sm text-muted-foreground">Build service pages that rank in Google.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for Worry-Free Website Hosting?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes fast, reliable hosting.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteHosting;
